// Sand Game JS; Patrik Harag, https://harag.cz; all rights reserved

/**
 *
 * @version 2026-09-01 / sm
 * @author Patrik Harag
 */

/**
 *
 * @param element {HTMLElement}
 * @param content {null|string|Node|(null|string|Node)[]}
 */
export function addContent(element, content) {
    if (content === null) {
        // ignore
    } else if (typeof content === 'string') {
        element.textContent = content;
    } else if (content instanceof Node) {
        element.appendChild(content);
    } else if (Array.isArray(content)) {
        for (const item of content) {
            if (item instanceof Node) {
                element.appendChild(item);
            } else if (typeof item === 'string') {
                element.insertAdjacentText('beforeend', item);
            } else if (item === null) {
                // ignore
            } else {
                throw 'Content type not supported: ' + (typeof item);
            }
        }
    } else {
        throw 'Content type not supported: ' + (typeof content);
    }
}

/**
 *
 * @param element {HTMLElement}
 * @param content {null|string|Node|(null|string|Node)[]}
 */
export function setContent(element, content) {
    element.innerHTML = '';
    addContent(element, content);
}

/**
 *
 * @param element {HTMLElement}
 * @param attributes {object|null}
 */
export function putAttributes(element, attributes) {
    if (attributes == null) {
        return;
    }
    for (const key in attributes) {
        const value = attributes[key];
        const type = typeof value;

        if (type === 'boolean') {
            if (value) {
                element.setAttribute(key, '');
            }
        } else if (type === 'string' || type === 'number') {
            element.setAttribute(key, value);
        } else if (key === 'style' && type === 'object') {
            Object.assign(element.style, value);
        } else if (value === null) {
            // ignore
        } else {
            throw 'Unsupported attribute type: ' + (typeof value);
        }
    }
}

/**
 *
 * @param html {string}
 * @return {HTMLElement}
 */
export function create(html) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.firstElementChild;
}

/**
 *
 * @param name {string}
 * @param attributes {object|null}
 * @param content {null|string|Node|(null|string|Node)[]}
 * @return {HTMLElement}
 */
export function element(name, attributes = null, content = null) {
    const element = document.createElement(name);

    // attributes
    putAttributes(element, attributes);

    // content
    addContent(element, content);

    return element;
}

/**
 *
 * @param attributes {object|null}
 * @param content {null|string|Node|(null|string|Node)[]}
 * @return {HTMLElement}
 */
export function div(attributes = null, content = null) {
    return element('div', attributes, content);
}

/**
 *
 * @param attributes {object|null}
 * @param content {null|string|Node|(null|string|Node)[]}
 * @return {HTMLElement}
 */
export function par(attributes = null, content = null) {
    return element('p', attributes, content);
}

/**
 *
 * @param text {string|null}
 * @param attributes {object|null}
 * @return {HTMLElement}
 */
export function span(text = null, attributes = null) {
    return element('span', attributes, text);
}

/**
 *
 * @param label {null|string|Node|(null|string|Node)[]}
 * @param attributes {object|null}
 * @param handler {function()}
 * @return {HTMLElement}
 */
export function link(label, attributes = null, handler = null) {
    const link = element('a', attributes, label);
    if (handler !== null) {
        link.href = 'javascript:void(0)';
        link.addEventListener('click', handler);
    }
    return link;
}

/**
 *
 * @param label {null|string|Node|(null|string|Node)[]}
 * @param attributes {object|null}
 * @param handler {function()}
 * @return {HTMLElement}
 */
export function button(label, attributes = null, handler = null) {
    if (attributes === null) {
        attributes = {};
    }
    attributes['type'] = 'button';

    const button = element('button', attributes, label);
    if (handler !== null) {
        button.addEventListener('click', handler);
    }
    return button;
}

// bootstrap methods

/**
 *
 * @param alertClass {"alert-info"|"alert-warn"|"alert-danger"}
 * @param bodyContent {null|string|Node|(null|string|Node)[]}
 * @return {HTMLElement}
 */
export function bootstrapAlert(alertClass, bodyContent) {
    const alertDiv = div({ class: 'alert ' + alertClass + ' alert-dismissible fade show', role: 'alert' });
    addContent(alertDiv, bodyContent);
    alertDiv.append(button(null, { type: 'button', class: 'btn-close', 'data-bs-dismiss': 'alert', 'aria-label': 'Close' }));
    return alertDiv;
}

/**
 *
 * @param headerContent {null|string|Node|(null|string|Node)[]}
 * @param bodyContent {null|string|Node|(null|string|Node)[]}
 * @param attributes {object|null}
 * @return {HTMLElement}
 */
export function bootstrapCard(headerContent, bodyContent, attributes = null) {
    if (attributes === null) {
        attributes = {};
    }
    attributes.class = 'card ' + attributes.class;

    const card = div(attributes);

    if (headerContent) {
        card.append(div({ class: 'card-header' }, headerContent));
    }

    card.append(div({ class: 'card-body' }, bodyContent));
    return card;
}

/**
 *
 * @param title {string}
 * @param collapsed {boolean}
 * @param bodyContent {null|string|Node|(null|string|Node)[]}
 * @return {HTMLElement}
 */
export function bootstrapCardCollapsable(title, collapsed, bodyContent) {
    const id = 'collapsable_' + Math.floor(Math.random() * 999_999_999);

    return div({ class: 'card' }, [
        div({ class: 'card-header' }, [
            element('a', { class: 'card-link', 'data-bs-toggle': 'collapse', href: '#' + id}, title)
        ]),
        div({ id: id, class: (collapsed ? 'collapse' : 'collapse show') }, [
            div({ class: 'card-body' }, bodyContent)
        ])
    ]);
}

/**
 *
 * @param content {string|HTMLElement}
 * @param node {HTMLElement}
 * @return {HTMLElement}
 */
export function bootstrapInitTooltip(content, node) {
    if (window.bootstrap === undefined) {
        console.error('Bootstrap library not available');
        return node;
    }

    const old = new window.bootstrap.Tooltip(node);
    if (old) {
        old.dispose();
    }

    node.setAttribute('data-bs-toggle', 'tooltip');
    node.setAttribute('data-bs-placement', 'top');
    if (typeof content === 'object') {
        node.setAttribute('data-bs-html', 'true');
        node.setAttribute('title', content.innerHTML);
    } else {
        node.setAttribute('title', content);
    }

    new window.bootstrap.Tooltip(node);
    return node;
}

/**
 *
 * @param text {string}
 * @param checked {boolean}
 * @param handler {function(boolean)}
 * @return {HTMLElement}
 */
export function bootstrapSwitchButton(text, checked, handler = null) {
    const id = 'switch-button_' + Math.floor(Math.random() * 999_999_999);

    const switchInput = element('input', {
        type: 'checkbox',
        id: id,
        class: 'form-check-input',
        role: 'switch',
    });
    if (checked === true) {
        switchInput.checked = true;
    }

    const control = div({ class: 'form-check form-switch', style: 'display: inline-block' }, [
        switchInput,
        element('label', { class: 'form-check-label', for: id }, text)
    ]);

    if (handler !== null) {
        switchInput.addEventListener('click', () => {
            handler(switchInput.checked);
        });
    }
    return control;
}

/**
 *
 * @param labelContent {string|HTMLElement}
 * @param buttonClass {string} e.g. btn-primary
 * @param checked {boolean}
 * @param handler {function(boolean)}
 * @return {HTMLElement[]}
 */
export function bootstrapToggleButton(labelContent, buttonClass, checked, handler = null) {
    const id = 'toggle-button_' + Math.floor(Math.random() * 999_999_999);

    const nodeInput = element('input', {
        type: 'checkbox',
        class: 'btn-check',
        checked: checked,
        id: id
    });
    const nodeLabel = element('label', {
        class: 'btn ' + buttonClass,
        for: id
    }, labelContent);

    nodeInput.addEventListener('change', (e) => {
        if (handler !== null) {
            handler(nodeInput.checked);
        }
    });

    return [nodeInput, nodeLabel];
}

export function bootstrapTableBuilder() {
    return new BootstrapTable();
}

export function bootstrapDialogBuilder() {
    return new BootstrapDialog();
}

/**
 *
 * @version 2024-08-16
 * @author Patrik Harag
 */
class BootstrapTable {

    #tableBody = element('tbody');

    addRow(row) {
        this.#tableBody.appendChild(row);
    }

    addRowBefore(row) {
        this.#tableBody.insertBefore(row, this.#tableBody.firstChild);
    }

    createNode() {
        const table = element('table', { class: 'table table-striped' });
        table.appendChild(this.#tableBody);

        const tableResponsive = div({ class: 'table-responsive' });
        tableResponsive.appendChild(table);

        return tableResponsive;
    }
}

/**
 *
 * @version 2025-02-12
 * @author Patrik Harag
 */
class BootstrapDialog {

    // will be removed after close
    #persistent = false;

    #additionalStyle = '';

    #headerNode = null;
    #bodyNode = null;
    #footerNodeChildren = [];

    #dialog = null;
    #dialogBootstrap = null;

    #onShown = [];

    setPersistent(persistent) {
        this.#persistent = persistent;
    }

    setAdditionalStyle(style) {
        this.#additionalStyle = style;
    }

    setSizeLarge() {
        this.#additionalStyle = 'modal-lg';
    }

    setSizeExtraLarge() {
        this.#additionalStyle = 'modal-xl';
    }

    setHeaderContent(headerNode) {
        if (typeof headerNode === 'string') {
            this.#headerNode = element('strong', null, headerNode);
        } else {
            this.#headerNode = headerNode;
        }
    }

    setBodyContent(bodyNode) {
        this.#bodyNode = bodyNode;
    }

    addCloseButton(buttonText, focus = false) {
        const button = element('button', { type: 'button', class: 'btn btn-secondary', 'data-bs-dismiss': 'modal' }, buttonText);
        this.addButton(button, focus);
    }

    addSubmitButton(buttonText, handler, focus = false) {
        const button = element('button', { type: 'button', class: 'btn btn-primary', 'data-bs-dismiss': 'modal' }, buttonText);
        button.addEventListener('click', handler);
        this.addButton(button, focus);
    }

    addButton(button, focus = false) {
        this.#footerNodeChildren.push(button);
        if (focus) {
            this.#onShown.push(function () {
                button.focus();
            });
        }
    }

    show(dialogAnchor, fade = true, centered = true) {
        if (window.bootstrap === undefined) {
            console.error('Bootstrap library not available');
            return;
        }

        let modalClasses = 'modal';
        if (fade) {
            modalClasses += ' fade';
        }

        let dialogClasses = 'modal-dialog';
        if (centered) {
            dialogClasses += ' modal-dialog-centered';
        }
        dialogClasses += ' ' + this.#additionalStyle;

        if (this.#dialog === null) {
            this.#dialog = div({ class: modalClasses, tabindex: '-1', role: 'dialog', 'aria-hidden': 'true' }, [
                div({ class: dialogClasses }, [
                    div({ class: 'modal-content' }, [
                        this.#headerNode !== null ? div({ class: 'modal-header' }, this.#headerNode) : null,
                        div({ class: 'modal-body' }, this.#bodyNode),
                        this.#footerNodeChildren.length > 0 ? div({ class: 'modal-footer' }, this.#footerNodeChildren) : null
                    ])
                ])
            ]);

            // add into DOM
            dialogAnchor.appendChild(this.#dialog);
        }

        this.#dialogBootstrap = new window.bootstrap.Modal(this.#dialog);

        if (!this.#persistent) {
            // remove from DOM after hide
            this.#dialog.addEventListener('hidden.bs.modal', () => {
                dialogAnchor.removeChild(this.#dialog);
            });
        }

        this.#dialog.addEventListener('shown.bs.modal', () => {
            for (let handler of this.#onShown) {
                handler();
            }
        });

        this.#dialogBootstrap.show();

        return this.#dialog;
    }

    hide() {
        if (this.#dialog !== null) {
            this.#dialogBootstrap.hide();
        }
    }
}
